import {
  type HTMLAttributes,
  type TdHTMLAttributes,
  type ThHTMLAttributes,
  forwardRef,
} from 'react'

import { classNames } from '@/utils/helpers'

/** Props for customizing the behavior or appearance of a table element. */
type TableProps = HTMLAttributes<HTMLTableElement> & {
  stickyHeaderSizeProp?: string
  testId?: string
}
/** Props for manipulating or styling individual rows within a table. */
type TableRowProps = HTMLAttributes<HTMLTableRowElement>
/** Props for customizing the content or behavior of individual table cells. */
type TableCellProps = TdHTMLAttributes<HTMLTableCellElement>
/** Props for modifying the appearance or behavior of table header cells. */
type TableCellHeaderProps = ThHTMLAttributes<HTMLTableCellElement>
/** Props for targeting specific sections (thead, tbody, tfoot) of a table. */
type TableSectionProps = HTMLAttributes<HTMLTableSectionElement>

/**
 * A wrapper component for the native HTML table element.
 * For advanced data structure, see "/ui/Table/DataTable.tsx"
 */
export const Table = forwardRef<HTMLTableElement, TableProps>(
  ({ className, stickyHeaderSizeProp, testId, ...props }, ref) => (
    <div
      className="w-full overflow-auto text-secondary-300"
      style={{
        tableLayout: stickyHeaderSizeProp ? 'fixed' : undefined,
        height: stickyHeaderSizeProp ? stickyHeaderSizeProp : undefined,
      }}
    >
      <table
        ref={ref}
        className={classNames('w-full text-sm', className)}
        data-testid={testId}
        {...props}
      />
    </div>
  ),
)

Table.displayName = 'Table'

export const TableHeader = forwardRef<
  HTMLTableSectionElement,
  TableSectionProps
>(({ className, ...props }, ref) => (
  <thead
    ref={ref}
    className={classNames('opacity-70 [&_tr]:border-b', className)}
    {...props}
  />
))

TableHeader.displayName = 'TableHeader'

export const TableBody = forwardRef<HTMLTableSectionElement, TableSectionProps>(
  ({ className, ...props }, ref) => (
    <tbody
      ref={ref}
      className={classNames('[&_tr:last-child]:border-0', className)}
      {...props}
    />
  ),
)
TableBody.displayName = 'TableBody'

export const TableFooter = forwardRef<
  HTMLTableSectionElement,
  TableSectionProps
>(({ className, ...props }, ref) => (
  <tfoot
    ref={ref}
    className={classNames(
      'bg-primary-500 font-medium text-primary-500',
      className,
    )}
    {...props}
  />
))

TableFooter.displayName = 'TableFooter'

export const TableRow = forwardRef<HTMLTableRowElement, TableRowProps>(
  ({ className, ...props }, ref) => (
    <tr
      ref={ref}
      className={classNames('border-b transition-colors', className)}
      {...props}
    />
  ),
)

TableRow.displayName = 'TableRow'

export const TableHead = forwardRef<HTMLTableCellElement, TableCellHeaderProps>(
  ({ className, ...props }, ref) => (
    <th
      ref={ref}
      className={classNames(
        'h-12 px-4 text-left align-middle font-medium',
        className,
      )}
      {...props}
    />
  ),
)

TableHead.displayName = 'TableHead'

export const TableCell = forwardRef<HTMLTableCellElement, TableCellProps>(
  ({ className, ...props }, ref) => (
    <td
      ref={ref}
      className={classNames(
        'p-5 align-middle font-medium text-title',
        className,
      )}
      {...props}
    />
  ),
)

TableCell.displayName = 'TableCell'
